import React from 'react';
import { Shield, FileCheck, Globe, Lock, CheckCircle, Award, Scale, Users, Database, AlertCircle, Download, ExternalLink } from 'lucide-react';

const Compliance = () => {
  const complianceFrameworks = [
    {
      icon: <Award className="w-12 h-12 text-[#854DFF]" />,
      title: 'SOC 2 Type II',
      status: 'Certified',
      lastAudit: '2024',
      description: 'Annual independent audit of security, availability, processing integrity, confidentiality, and privacy controls',
      controls: [
        'Security controls and policies',
        'Availability and performance monitoring',
        'Processing integrity verification',
        'Confidentiality of sensitive data',
        'Privacy protection mechanisms'
      ],
      reportAvailable: true
    },
    {
      icon: <Globe className="w-12 h-12 text-[#854DFF]" />,
      title: 'GDPR Compliance',
      status: 'Compliant',
      lastAudit: '2024',
      description: 'Full compliance with EU General Data Protection Regulation for data protection and privacy',
      controls: [
        'Data subject rights management',
        'Lawful basis for data processing',
        'Data portability and export',
        'Right to erasure (right to be forgotten)',
        'Data breach notification procedures',
        'Data Protection Impact Assessments (DPIA)',
        'Privacy by design and default',
        'Appointment of Data Protection Officer'
      ],
      reportAvailable: true
    },
    {
      icon: <FileCheck className="w-12 h-12 text-[#854DFF]" />,
      title: 'ISO 27001:2022',
      status: 'Certified',
      lastAudit: '2024',
      description: 'International standard for information security management systems',
      controls: [
        'Information security policies',
        'Asset management',
        'Access control',
        'Cryptography controls',
        'Physical and environmental security',
        'Operations security',
        'Communications security',
        'Incident management'
      ],
      reportAvailable: true
    },
    {
      icon: <Scale className="w-12 h-12 text-[#854DFF]" />,
      title: 'CCPA Compliance',
      status: 'Compliant',
      lastAudit: '2024',
      description: 'California Consumer Privacy Act compliance for California residents',
      controls: [
        'Consumer right to know',
        'Consumer right to delete',
        'Consumer right to opt-out',
        'Non-discrimination provisions',
        'Notice at collection',
        'Privacy policy requirements'
      ],
      reportAvailable: false
    },
    {
      icon: <Lock className="w-12 h-12 text-[#854DFF]" />,
      title: 'PCI DSS Level 1',
      status: 'Compliant',
      lastAudit: '2024',
      description: 'Payment Card Industry Data Security Standard for secure payment processing',
      controls: [
        'Secure network architecture',
        'Cardholder data protection',
        'Vulnerability management',
        'Access control measures',
        'Network monitoring and testing',
        'Security policy maintenance'
      ],
      reportAvailable: true
    },
    {
      icon: <FileCheck className="w-12 h-12 text-[#854DFF]" />,
      title: 'HIPAA Compliance',
      status: 'Compliant',
      lastAudit: '2024',
      description: 'Health Insurance Portability and Accountability Act for healthcare data',
      controls: [
        'Administrative safeguards',
        'Physical safeguards',
        'Technical safeguards',
        'Business Associate Agreements',
        'Breach notification procedures',
        'Patient rights protection'
      ],
      reportAvailable: true
    }
  ];

  const regionalCompliance = [
    {
      region: 'United States',
      frameworks: [
        { name: 'CCPA', description: 'California Consumer Privacy Act' },
        { name: 'CPRA', description: 'California Privacy Rights Act' },
        { name: 'HIPAA', description: 'Health Insurance Portability and Accountability Act' },
        { name: 'SOX', description: 'Sarbanes-Oxley Act for financial reporting' },
        { name: 'GLBA', description: 'Gramm-Leach-Bliley Act for financial institutions' }
      ]
    },
    {
      region: 'European Union',
      frameworks: [
        { name: 'GDPR', description: 'General Data Protection Regulation' },
        { name: 'NIS2 Directive', description: 'Network and Information Security Directive' },
        { name: 'DORA', description: 'Digital Operational Resilience Act' },
        { name: 'ePrivacy Directive', description: 'Privacy and Electronic Communications' }
      ]
    },
    {
      region: 'United Kingdom',
      frameworks: [
        { name: 'UK GDPR', description: 'UK General Data Protection Regulation' },
        { name: 'DPA 2018', description: 'Data Protection Act 2018' },
        { name: 'PECR', description: 'Privacy and Electronic Communications Regulations' }
      ]
    },
    {
      region: 'Asia-Pacific',
      frameworks: [
        { name: 'PDPA (Singapore)', description: 'Personal Data Protection Act' },
        { name: 'APPI (Japan)', description: 'Act on Protection of Personal Information' },
        { name: 'PIPEDA (Canada)', description: 'Personal Information Protection and Electronic Documents Act' },
        { name: 'Privacy Act (Australia)', description: 'Australian Privacy Principles' }
      ]
    }
  ];

  const dataProcessingPrinciples = [
    {
      title: 'Lawfulness, Fairness, and Transparency',
      description: 'All data processing is conducted lawfully, fairly, and in a transparent manner with clear communication to data subjects.'
    },
    {
      title: 'Purpose Limitation',
      description: 'Personal data is collected for specified, explicit, and legitimate purposes and not further processed in a manner incompatible with those purposes.'
    },
    {
      title: 'Data Minimization',
      description: 'We collect only the personal data that is adequate, relevant, and limited to what is necessary for the purposes of processing.'
    },
    {
      title: 'Accuracy',
      description: 'Personal data is kept accurate and up to date. Inaccurate data is erased or rectified without delay.'
    },
    {
      title: 'Storage Limitation',
      description: 'Personal data is retained only for as long as necessary for the purposes for which it was collected.'
    },
    {
      title: 'Integrity and Confidentiality',
      description: 'Data is processed in a manner that ensures appropriate security, including protection against unauthorized access, loss, or damage.'
    },
    {
      title: 'Accountability',
      description: 'We are responsible for and can demonstrate compliance with all data protection principles.'
    }
  ];

  const dataSubjectRights = [
    {
      right: 'Right to Access',
      description: 'Request access to your personal data and receive a copy',
      responseTime: 'Within 30 days'
    },
    {
      right: 'Right to Rectification',
      description: 'Request correction of inaccurate or incomplete personal data',
      responseTime: 'Within 30 days'
    },
    {
      right: 'Right to Erasure',
      description: 'Request deletion of your personal data ("right to be forgotten")',
      responseTime: 'Within 30 days'
    },
    {
      right: 'Right to Data Portability',
      description: 'Receive your personal data in a structured, machine-readable format',
      responseTime: 'Within 30 days'
    },
    {
      right: 'Right to Object',
      description: 'Object to processing of your personal data for specific purposes',
      responseTime: 'Immediately'
    },
    {
      right: 'Right to Restrict Processing',
      description: 'Request restriction of processing under certain conditions',
      responseTime: 'Within 30 days'
    },
    {
      right: 'Right to Withdraw Consent',
      description: 'Withdraw consent for data processing at any time',
      responseTime: 'Immediately'
    },
    {
      right: 'Right to Lodge a Complaint',
      description: 'File a complaint with supervisory authorities',
      responseTime: 'N/A'
    }
  ];

  const auditAndCertification = [
    {
      category: 'Annual Security Audits',
      items: [
        'SOC 2 Type II audit by independent third-party auditors',
        'ISO 27001 surveillance and recertification audits',
        'PCI DSS Annual Report on Compliance (ROC)',
        'Penetration testing by certified ethical hackers',
        'Vulnerability assessments and remediation tracking'
      ]
    },
    {
      category: 'Continuous Monitoring',
      items: [
        '24/7 security operations center (SOC)',
        'Real-time threat detection and response',
        'Automated compliance monitoring tools',
        'Regular internal security assessments',
        'Quarterly risk assessments'
      ]
    },
    {
      category: 'Documentation & Records',
      items: [
        'Data processing agreements (DPA)',
        'Business Associate Agreements (BAA)',
        'Records of processing activities (ROPA)',
        'Data protection impact assessments (DPIA)',
        'Incident response and breach notification logs',
        'Employee training and awareness records'
      ]
    }
  ];

  const vendorManagement = [
    {
      title: 'Vendor Security Assessment',
      description: 'All third-party vendors undergo rigorous security assessments before onboarding',
      requirements: [
        'Security questionnaires and due diligence',
        'Review of certifications (SOC 2, ISO 27001)',
        'Data processing agreement execution',
        'Regular vendor risk assessments',
        'Contractual security and privacy requirements'
      ]
    },
    {
      title: 'Subprocessor Management',
      description: 'Transparent management of all subprocessors handling customer data',
      requirements: [
        'Public subprocessor list maintenance',
        '30-day advance notice for new subprocessors',
        'Customer right to object to subprocessors',
        'Contractual flow-down of data protection obligations',
        'Regular subprocessor audit and monitoring'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#854DFF] to-[#7A42E6] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="flex justify-center mb-6">
              <Scale className="w-20 h-20" />
            </div>
            <h1 className="text-5xl font-bold mb-6">
              Regulatory Compliance & Certifications
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              CredFlexi maintains the highest standards of regulatory compliance across multiple jurisdictions 
              and industries, ensuring your data is protected and your business meets all applicable requirements.
            </p>
          </div>
        </div>
      </section>

      {/* Compliance Frameworks */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Compliance Certifications
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Independently audited and certified to meet global compliance standards
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {complianceFrameworks.map((framework, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-all duration-300"
              >
                <div className="mb-6">{framework.icon}</div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-bold text-gray-900">
                    {framework.title}
                  </h3>
                  <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-semibold rounded-full">
                    {framework.status}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mb-4">Last Audit: {framework.lastAudit}</p>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {framework.description}
                </p>
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Key Controls:</h4>
                  <ul className="space-y-2">
                    {framework.controls.map((control, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{control}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                {framework.reportAvailable && (
                  <button className="mt-6 w-full flex items-center justify-center px-4 py-2 border border-[#854DFF] text-[#854DFF] rounded-lg hover:bg-[#854DFF] hover:text-white transition-all duration-200">
                    <Download className="w-4 h-4 mr-2" />
                    Request Compliance Report
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Regional Compliance */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Global Regulatory Compliance
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Compliance with data protection and privacy regulations across major jurisdictions
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {regionalCompliance.map((region, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl shadow-sm p-8 border border-gray-100"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <Globe className="w-6 h-6 text-[#854DFF] mr-3" />
                  {region.region}
                </h3>
                <div className="space-y-4">
                  {region.frameworks.map((framework, idx) => (
                    <div key={idx} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-gray-900">{framework.name}</h4>
                        <p className="text-sm text-gray-600">{framework.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Processing Principles */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Data Processing Principles
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our commitment to responsible and lawful data processing
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {dataProcessingPrinciples.map((principle, index) => (
              <div 
                key={index}
                className="bg-white p-6 rounded-lg border-2 border-[#854DFF]/20 hover:border-[#854DFF] transition-all duration-300"
              >
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  {principle.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {principle.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Subject Rights */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Your Data Protection Rights
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We respect and facilitate all data subject rights under GDPR and other privacy regulations
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {dataSubjectRights.map((item, index) => (
              <div 
                key={index}
                className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center">
                    <Users className="w-5 h-5 text-[#854DFF] mr-2" />
                    {item.right}
                  </h3>
                  <span className="text-xs bg-[#854DFF]/10 text-[#854DFF] px-2 py-1 rounded">
                    {item.responseTime}
                  </span>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <a
              href="/contact"
              className="inline-flex items-center bg-gradient-to-r from-[#854DFF] to-[#7A42E6] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:from-[#7A42E6] hover:to-[#6F37CC] transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Exercise Your Data Rights
              <ExternalLink className="ml-2 w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Audit & Certification */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Audit & Certification Program
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Continuous validation of our security and compliance posture
            </p>
          </div>

          <div className="space-y-8">
            {auditAndCertification.map((section, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl shadow-sm p-8 border border-gray-100"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <Award className="w-6 h-6 text-[#854DFF] mr-3" />
                  {section.category}
                </h3>
                <ul className="grid md:grid-cols-2 gap-4">
                  {section.items.map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vendor Management */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Third-Party Vendor Management
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Rigorous oversight of all vendors and subprocessors handling your data
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {vendorManagement.map((section, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl shadow-lg p-8 border border-gray-100"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {section.title}
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {section.description}
                </p>
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-4">Requirements:</h4>
                  <ul className="space-y-3">
                    {section.requirements.map((req, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Contact */}
      <section className="py-20 bg-gradient-to-r from-[#854DFF] to-[#7A42E6]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <AlertCircle className="w-16 h-16 mx-auto mb-6" />
          <h2 className="text-3xl font-bold mb-4">
            Compliance Questions or Concerns?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Our compliance team is here to help with any questions about our certifications, 
            data processing practices, or regulatory requirements.
          </p>
          <div className="space-y-3">
            <p className="text-lg">
              <strong>Data Protection Officer:</strong> dpo@credflexi.com
            </p>
            <p className="text-lg">
              <strong>Compliance Team:</strong> compliance@credflexi.com
            </p>
            <p className="text-lg">
              <strong>Response Time:</strong> Within 48 hours
            </p>
          </div>
        </div>
      </section>

      {/* Documentation CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Request Compliance Documentation
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Access our audit reports, certifications, DPAs, BAAs, and other compliance documentation
          </p>
          <a
            href="/contact"
            className="inline-flex items-center bg-gradient-to-r from-[#854DFF] to-[#7A42E6] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:from-[#7A42E6] hover:to-[#6F37CC] transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <Download className="mr-2 w-5 h-5" />
            Request Documentation
          </a>
        </div>
      </section>
    </div>
  );
};

export default Compliance;
